import { useState } from "react";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { MapPin, Phone, Mail, Clock, Building2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contatti() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Messaggio inviato! Ti contatteremo a breve.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Errore nell'invio. Riprova o chiamaci direttamente.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contatti-page" className="min-h-screen bg-white">
      <Header />

      <section className="pt-32 pb-20 max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#5ab500] font-bold mb-4">
          — Contatti
        </div>
        <h1 className="font-heading text-5xl md:text-6xl font-light tracking-tight text-[#1c1f1a]">
          Veniamo a <span className="italic text-[#5ab500]">conoscerci</span>.
        </h1>
        <p className="mt-4 text-[#7a7f77] max-w-2xl">
          Scrivici, chiamaci o passa a trovarci in vetrina. Siamo qui per
          rispondere a ogni tua domanda senza impegno.
        </p>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2 space-y-5">
            {[
              {
                icon: MapPin,
                title: "Negozio",
                body: "Via Puccini 5\nZibido San Giacomo (MI) 20058",
              },
              {
                icon: Building2,
                title: "Sede Fiscale",
                body: "V. Giuseppe Dezza, 24\n20144 Milano\nP.IVA 14669860968\nPEC: auto.licari@legalmail.it",
              },
              {
                icon: Clock,
                title: "I Nostri Orari",
                body: "Lun–Ven: 9:00–19:00\nSabato: 9:00–14:00",
              },
            ].map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                className="bg-[#f5f6f4] rounded-2xl p-6 border border-black/[0.04]"
              >
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-[#5ab500]/10 flex items-center justify-center flex-shrink-0">
                    <Icon size={18} className="text-[#5ab500]" />
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-[#5ab500] font-bold mb-1">
                      {title}
                    </div>
                    <div className="text-[#1c1f1a] whitespace-pre-line">{body}</div>
                  </div>
                </div>
              </div>
            ))}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <a
                href="tel:+393297452439"
                data-testid="contatti-phone-mobile"
                className="bg-[#1c1f1a] text-white rounded-2xl p-5 flex items-center gap-3 hover:bg-[#5ab500] transition"
              >
                <Phone size={18} />
                <div>
                  <div className="text-[10px] uppercase tracking-wider opacity-70">
                    Cellulare
                  </div>
                  <div className="text-sm font-bold">CEL. 329 745 2439</div>
                </div>
              </a>
              <a
                href="tel:+390243511941"
                data-testid="contatti-phone"
                className="bg-[#1c1f1a] text-white rounded-2xl p-5 flex items-center gap-3 hover:bg-[#5ab500] transition"
              >
                <Phone size={18} />
                <div>
                  <div className="text-[10px] uppercase tracking-wider opacity-70">
                    Fisso
                  </div>
                  <div className="text-sm font-bold">TEL. 02 4351 1941</div>
                </div>
              </a>
              <a
                href="mailto:auto.licari26@gmail.com"
                data-testid="contatti-email"
                className="sm:col-span-2 bg-white border border-black/[0.08] text-[#1c1f1a] rounded-2xl p-5 flex items-center gap-3 hover:border-[#5ab500] transition min-w-0 shadow-sm"
              >
                <Mail size={18} className="text-[#5ab500] flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-[#7a7f77]">
                    Email
                  </div>
                  <div className="text-sm font-bold truncate">
                    auto.licari26@gmail.com
                  </div>
                </div>
              </a>
            </div>
          </div>

          <div className="lg:col-span-3">
            <form
              onSubmit={submit}
              data-testid="general-contact-form"
              className="bg-[#f5f6f4] rounded-3xl p-8 border border-black/[0.05]"
            >
              <h2 className="font-heading text-2xl text-[#1c1f1a] font-semibold mb-1">
                Scrivici un messaggio
              </h2>
              <p className="text-sm text-[#7a7f77] mb-6">
                Ti risponderemo entro 24 ore lavorative.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="text" required placeholder="Nome e cognome *"
                  data-testid="general-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={inputCls}
                />
                <input
                  type="email" required placeholder="Email *"
                  data-testid="general-email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={inputCls}
                />
              </div>
              <input
                type="tel" placeholder="Telefono (opzionale)"
                data-testid="general-phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className={`mt-4 ${inputCls}`}
              />
              <textarea
                required rows={6} placeholder="Scrivici la tua richiesta... *"
                data-testid="general-message"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className={`mt-4 ${inputCls} resize-none`}
              />
              <button
                type="submit" disabled={sending}
                data-testid="general-submit"
                className="mt-5 px-8 py-3.5 rounded-full bg-[#5ab500] text-white font-bold hover:bg-[#4a9a00] hover:shadow-[0_8px_24px_rgba(90,181,0,0.4)] disabled:opacity-60 transition-all"
              >
                {sending ? "Invio in corso..." : "Invia messaggio"}
              </button>
            </form>
          </div>
        </div>

        {/* Map */}
        <div className="mt-14">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#5ab500] font-bold mb-4">
            — Come raggiungerci
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-light text-[#1c1f1a] mb-6">
            Vienici a <span className="italic text-[#5ab500]">trovare</span>
          </h2>
          <div
            data-testid="contatti-map"
            className="relative rounded-3xl overflow-hidden border border-black/[0.08] aspect-[16/9] md:aspect-[21/9] shadow-[var(--shadow-card)]"
          >
            <iframe
              title="Mappa Minicarmobilità"
              src="https://www.google.com/maps?q=Via+Puccini+5,+Zibido+San+Giacomo,+MI,+20058&output=embed"
              className="absolute inset-0 w-full h-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
          <a
            href="https://www.google.com/maps/dir/?api=1&destination=Via+Puccini+5,+Zibido+San+Giacomo,+MI,+20058"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contatti-directions"
            className="inline-flex items-center gap-2 mt-5 px-6 py-3 rounded-full bg-[#5ab500] text-white text-sm font-bold hover:bg-[#4a9a00] hover:shadow-[0_8px_24px_rgba(90,181,0,0.4)] transition"
          >
            Apri in Google Maps →
          </a>
        </div>
      </section>

      <Footer />
    </main>
  );
}

const inputCls =
  "w-full px-4 py-3 rounded-xl bg-white border border-black/[0.08] text-sm text-[#1c1f1a] placeholder-[#7a7f77] focus:border-[#5ab500] outline-none transition";
